import React, { useState } from 'react';
import { IntelligenceShell, MonoNum } from '../components/intelligence/IntelligenceShell';
import { useIntelligenceReport, KpiCard, ChartCard, RangeControl, ExportMenu, ReportHeader, ReportSkeleton, downloadCsv, exportPdf, tooltipStyle, CARD } from '../components/intelligence/ReportKit';
import { AiSuggestion } from '../components/intelligence/AiSuggestion';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { Filter, TrendingDown, Smartphone, DollarSign, AlertTriangle, Sparkles } from 'lucide-react';

const STEP_LABELS = {
  widget_view: 'Widget viewed',
  service_view: 'Service opened',
  date_selected: 'Date picked',
  slot_selected: 'Time & duration',
  lead_entered: 'Details entered',
  checkout_started: 'Checkout started',
};

const INSIGHT_COPY = {
  biggest_drop: {
    icon: TrendingDown,
    title: 'Biggest drop-off',
    copy: (p) => `${p.dropPct}% of visitors drop between "${STEP_LABELS[p.fromStep] || p.fromStep}" and "${STEP_LABELS[p.toStep] || p.toStep}". Widening availability, simplifying that step, or trimming required fields usually recovers a slice of these.`,
  },
  worst_service: {
    icon: AlertTriangle,
    title: 'Under-converting service',
    copy: (p) => `${p.serviceName} converts at ${p.convPct}% vs a ${p.medianPct}% median across your services — its worst leak is "${p.worstDrop}". Review its price display, photos, durations or custom questions.`,
  },
  price_sensitivity: {
    icon: DollarSign,
    title: 'Price sensitivity',
    copy: (p) => `Sessions choosing ${p.cheapBand} options check out at ${p.cheapPct}% vs ${p.expPct}% for ${p.expBand}. Featuring shorter/cheaper durations more prominently could lift total conversions.`,
  },
  mobile_gap: {
    icon: Smartphone,
    title: 'Mobile conversion gap',
    copy: (p) => `Mobile visitors check out at ${p.mobilePct}% vs ${p.desktopPct}% on desktop — worth a run-through of the widget on a phone to spot friction.`,
  },
  low_traffic: {
    icon: Sparkles,
    title: 'Traffic is the bottleneck',
    copy: (p) => `Only ${p.views} widget sessions this period — conversion tuning matters less than getting more visitors in front of the widget right now.`,
  },
};

const FunnelBars = ({ funnel }) => {
  const max = funnel[0]?.sessions || 1;
  return (
    <div className="space-y-2.5" data-testid="funnel-bars">
      {funnel.map((f, i) => (
        <div key={f.step} className="flex items-center gap-3">
          <div className="w-32 sm:w-40 shrink-0 text-right text-[11px] sm:text-xs font-semibold text-slate-500">{STEP_LABELS[f.step] || f.step}</div>
          <div className="flex-1 h-8 bg-slate-100/70 rounded-lg overflow-hidden relative">
            <div
              className="h-full rounded-lg flex items-center px-2.5 transition-all duration-700"
              style={{ width: `${Math.max(4, (100 * f.sessions) / max)}%`, background: `linear-gradient(90deg, #2563EB, ${i < 2 ? '#3B82F6' : '#60A5FA'})` }}
            >
              <MonoNum className="text-[11px] font-bold text-white whitespace-nowrap">{f.sessions.toLocaleString()}</MonoNum>
            </div>
          </div>
          <div className="w-16 shrink-0 text-left">
            {i > 0 && <MonoNum className={`text-[11px] font-bold ${f.dropPct >= 50 ? 'text-rose-500' : 'text-slate-400'}`}>−{f.dropPct}%</MonoNum>}
          </div>
        </div>
      ))}
    </div>
  );
};

export default function BookingFunnelReport() {
  const [months, setMonths] = useState(3);
  const { data, loading } = useIntelligenceReport('booking-funnel', months);

  const exportCsv = () => {
    if (!data) return;
    downloadCsv([
      ['Step', 'Sessions', 'Drop %'],
      ...data.funnel.map((f) => [STEP_LABELS[f.step] || f.step, f.sessions, f.dropPct]),
      [],
      ['Service', 'Opened', 'Date', 'Time', 'Details', 'Checkout', 'Conv %', 'Worst drop'],
      ...data.services.map((s) => [s.name, s.views, s.dates, s.slots, s.leads, s.checkouts, s.convPct, s.worstDrop || '']),
    ], 'booking-funnel.csv');
  };
  const exportPdfDoc = () => {
    if (!data) return;
    exportPdf({
      filename: 'booking-funnel.pdf',
      title: 'Booking Funnel',
      subtitle: `Last ${months} months`,
      kpis: [
        ['Widget sessions', String(data.kpis.widgetViews)],
        ['Checkout sessions', String(data.kpis.checkoutSessions)],
        ['Checkout rate', `${data.kpis.checkoutRate}%`],
        ['Completed bookings', String(data.kpis.completedBookings)],
      ],
      tables: [{
        title: 'Per-service funnel',
        head: ['Service', 'Opened', 'Checkout', 'Conv %', 'Worst drop'],
        rows: data.services.map((s) => [s.name, s.views, s.checkouts, `${s.convPct}%`, s.worstDrop || '—']),
      }],
    });
  };

  return (
    <IntelligenceShell>
      <div className="px-4 sm:px-8 py-6 sm:py-8 max-w-6xl mx-auto" data-testid="booking-funnel-report">
        <ReportHeader
          title="Booking Funnel"
          blurb="Every anonymized step your visitors take inside the booking widget — where they drop off, by service, price and device."
          right={<><AiSuggestion report="booking-funnel" months={months} /><RangeControl months={months} setMonths={setMonths} prefix="funnel" /><ExportMenu onCsv={exportCsv} onPdf={exportPdfDoc} testid="funnel-export" /></>}
        />
        {loading ? <ReportSkeleton /> : !data?.hasData ? (
          <div className={`${CARD} p-10 text-center`} data-testid="funnel-empty">
            <Filter className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            <div className="font-bold text-slate-700 mb-1">No funnel data yet</div>
            <p className="text-sm text-slate-500 max-w-md mx-auto">
              Funnel tracking starts collecting automatically the next time your booking widget loads on your website.
              Check back after you've had some visitors.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
              <KpiCard label="Widget sessions" value={data.kpis.widgetViews.toLocaleString()} testid="funnel-kpi-views" />
              <KpiCard label="Checkout sessions" value={data.kpis.checkoutSessions.toLocaleString()} testid="funnel-kpi-checkouts" delay={40} />
              <KpiCard label="Checkout rate" value={`${data.kpis.checkoutRate}%`} testid="funnel-kpi-rate" delay={80} />
              <KpiCard label="Completed bookings" value={data.kpis.completedBookings.toLocaleString()} sub="via widget, from your bookings" testid="funnel-kpi-completed" delay={120} />
            </div>

            {data.insights?.length > 0 && (
              <div className="grid sm:grid-cols-2 gap-3 sm:gap-4 mb-6" data-testid="funnel-insights">
                {data.insights.map((ins, i) => {
                  const meta = INSIGHT_COPY[ins.kind];
                  if (!meta) return null;
                  const Icon = meta.icon;
                  return (
                    <div key={ins.kind} className={`${CARD} p-5 flex items-start gap-4 intel-rise`} style={{ animationDelay: `${i * 60}ms` }} data-testid={`funnel-insight-${ins.kind}`}>
                      <div className="w-10 h-10 rounded-full bg-[#EBF2FF] text-[#2563EB] flex items-center justify-center shrink-0">
                        <Icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-bold text-sm text-slate-900 mb-1">{meta.title}</div>
                        <p className="text-sm text-slate-500 leading-relaxed">{meta.copy(ins.params)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <ChartCard title="Conversion funnel" testid="funnel-chart" className="mb-6">
              <FunnelBars funnel={data.funnel} />
            </ChartCard>

            <div className="grid lg:grid-cols-2 gap-4 sm:gap-6 mb-6">
              <ChartCard title="Weekly sessions vs checkouts" testid="funnel-trend">
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={data.trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
                      <XAxis dataKey="week" tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} width={34} />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Bar dataKey="views" name="Sessions" fill="#BFDBFE" radius={[4, 4, 0, 0]} />
                      <Line dataKey="checkouts" name="Checkouts" stroke="#2563EB" strokeWidth={2} dot={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </ChartCard>
              <ChartCard title="Checkout rate by price & device" testid="funnel-bands">
                <div className="space-y-4">
                  {data.priceBands.length > 0 && (
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">By selected price</div>
                      {data.priceBands.map((b) => (
                        <div key={b.band} className="flex items-center gap-3 mb-1.5">
                          <div className="w-24 text-xs font-semibold text-slate-500">{b.band}</div>
                          <div className="flex-1 h-5 bg-slate-100/70 rounded-md overflow-hidden">
                            <div className="h-full bg-[#2563EB] rounded-md" style={{ width: `${Math.min(100, b.checkoutPct)}%` }} />
                          </div>
                          <MonoNum className="w-20 text-xs font-bold text-slate-700 text-right">{b.checkoutPct}% · {b.sessions}</MonoNum>
                        </div>
                      ))}
                    </div>
                  )}
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">By device</div>
                    <div className="flex gap-2 flex-wrap">
                      {Object.entries(data.devices).map(([d, v]) => (
                        <div key={d} className="px-3 py-2 rounded-xl bg-slate-50 ring-1 ring-slate-100" data-testid={`funnel-device-${d}`}>
                          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{d}</div>
                          <MonoNum className="text-sm font-extrabold text-slate-800">{v.checkoutPct}%</MonoNum>
                          <span className="text-[10px] text-slate-400 ml-1">of {v.sessions}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </ChartCard>
            </div>

            <ChartCard title="Per-service funnel — where each service leaks" testid="funnel-services">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                      <th className="py-2 pr-3">Service</th>
                      <th className="py-2 px-2 text-right">Opened</th>
                      <th className="py-2 px-2 text-right">Date</th>
                      <th className="py-2 px-2 text-right">Time</th>
                      <th className="py-2 px-2 text-right">Details</th>
                      <th className="py-2 px-2 text-right">Checkout</th>
                      <th className="py-2 px-2 text-right">Conv</th>
                      <th className="py-2 pl-2">Worst drop</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.services.map((s) => (
                      <tr key={s.serviceId} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`funnel-service-row-${s.serviceId}`}>
                        <td className="py-2.5 pr-3 font-semibold text-slate-800 max-w-[220px] truncate">{s.name}</td>
                        <td className="py-2.5 px-2 text-right"><MonoNum>{s.views}</MonoNum></td>
                        <td className="py-2.5 px-2 text-right"><MonoNum>{s.dates}</MonoNum></td>
                        <td className="py-2.5 px-2 text-right"><MonoNum>{s.slots}</MonoNum></td>
                        <td className="py-2.5 px-2 text-right"><MonoNum>{s.leads}</MonoNum></td>
                        <td className="py-2.5 px-2 text-right"><MonoNum>{s.checkouts}</MonoNum></td>
                        <td className="py-2.5 px-2 text-right"><MonoNum className={`font-bold ${s.convPct >= 10 ? 'text-emerald-600' : 'text-slate-700'}`}>{s.convPct}%</MonoNum></td>
                        <td className="py-2.5 pl-2 text-xs text-slate-500">{s.worstDrop ? `${s.worstDrop} (−${s.worstDropPct}%)` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ChartCard>
          </>
        )}
      </div>
    </IntelligenceShell>
  );
}
